<template>
  <div class="card mb-25 border-0 rounded-0 bg-white add-user-card">
    <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">
      <Form class="row g-3" :validation-schema="schema" @submit="sendOuvFer">
        <div class="col-lg-8 col-md-10 mt-4">
          <div class="col mb-3">
            <label class="d-block text-black fw-semibold mb-10">
              Date d'ouverture <span class="text-danger">*</span>
            </label>
            <Field
              name="dateOuverture"
              class="form-control shadow-none fs-md-15 text-black"
              type="datetime-local"
              :max="currentDateTime"
              v-model="dateOuverture"
              readonly
            />
            <ErrorMessage name="dateOuverture" class="text-danger" />
          </div>

          <div class="col mb-3">
            <label class="d-block text-black fw-semibold mb-10">Caisse <span class="text-danger">*</span></label>
            <Field name="caisse" v-slot="{ field }">
              <Multiselect
                v-model="caisse"
                :options="caisseOptions"
                :preserve-search="true"
                :multiple="false"
                :searchable="true"
                placeholder="Sélectionner la caisse"
                label="label"
                track-by="value"
                v-bind="field"
                @change="handleCaisseChange"
              />
            </Field>
            <ErrorMessage name="caisse" class="text-danger" />
            <div v-if="caisseOptions.length === 0" class="text-danger mt-2">
              Aucune caisse disponible : aucune caisse affectée à cet utilisateur, ou toutes sont déjà ouvertes.
            </div>
          </div>

          <div class="col mb-3" v-if="caisse">
            <label for="soldeOuverture" class="d-block text-black fw-semibold mb-10">
              Solde d'ouverture (Fcfa)
            </label>
            <input type="number" min="0" id="soldeOuverture" class="form-control"
              v-model.number="soldeOuverture" placeholder="Entrer le solde d'ouverture" />
            <small class="text-muted d-block" v-if="soldeDerniereFermeture !== null">
              Solde de la dernière fermeture : <strong>{{ soldeDerniereFermeture.toLocaleString('fr-FR') }} Fcfa</strong>.
              Vous pouvez saisir un autre montant.
            </small>
            <small class="text-muted d-block" v-else>
              Première ouverture de cette caisse : saisissez l'argent présent dans le tiroir.
            </small>
            <small class="text-warning d-block" v-if="soldeModifie">
              Ce solde est différent de celui de la dernière fermeture.
            </small>
            <div v-if="soldeInvalide" class="text-danger">Le solde d'ouverture doit être un nombre positif ou nul.</div>
          </div>

          <div class="col-md-12 mb-3">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Personnes présentes à l'ouverture <span class="text-danger">*</span>
              </label>
              <Field name="user" v-slot="{ field }">
                <Multiselect
                  v-model="user"
                  :options="userOptions"
                  :searchable="true"
                  track-by="value"
                  mode="tags"
                  label="label"
                  placeholder="Sélectionner la personne"
                  v-bind="field"
                />
              </Field>
              <ErrorMessage name="user" class="text-danger" />
            </div>
          </div>

          <div class="col-md-12">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">Observation</label>
              <Field
                name="observations"
                as="textarea"
                cols="30"
                rows="8"
                placeholder="Entrer votre observation"
                class="form-control shadow-none rounded-0 text-black"
              />
              <ErrorMessage name="observations" class="text-danger" />
            </div>
          </div>

          <div class="mb-3 mt-1">
            <button
              type="submit"
              class="btn btn-primary top-end"
              :disabled="caisseOptions.length === 0 || isSubmitting || soldeInvalide"
            >
              <span v-if="isSubmitting" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              Ouvrir
            </button>
            <router-link to="/OuverturesFermetures/" type="button" class="btn btn-danger mx-1">Annuler</router-link>
          </div>
        </div>
      </Form>
    </div>
  </div>
</template>

<script lang="ts">
import { ref, computed, defineComponent, onMounted, nextTick } from 'vue';
import * as Yup from 'yup';
import { configure, Form, Field, ErrorMessage } from 'vee-validate';
import Multiselect from '@vueform/multiselect';
import ApiService from '@/services/ApiService';
import router from '@/router';
import { Caisse } from '@/models/Caisse';
import { error, monSwal, success } from '@/utils/utils';
import { getUser } from '@/services/JwtService';

export default defineComponent({
  name: 'AddOuvFerm',
  components: {
    Form,
    Field,
    ErrorMessage,
    Multiselect,
  },

  setup() {
    const userOptions = ref<{ value: number; label: string }[]>([]);
    const user = ref<number[]>([]);
    const dateOuverture = ref(getCurrentDateTime());
    const currentDateTime = ref(getCurrentDateTime());
    const soldeOuverture = ref<number | string>(0);
    const soldeDerniereFermeture = ref<number | null>(null); // null = première ouverture
    const soldeInvalide = computed(
      () => soldeOuverture.value === '' || isNaN(Number(soldeOuverture.value)) || Number(soldeOuverture.value) < 0
    );
    const soldeModifie = computed(
      () => soldeDerniereFermeture.value !== null && !soldeInvalide.value &&
            Number(soldeOuverture.value) !== soldeDerniereFermeture.value
    );
    const caisseOptions = ref<{ value: number; label: string }[]>([]);
    const caisse = ref<number | null>(null);
    const caisses = ref<Caisse[]>([]);
    const isSubmitting = ref<boolean>(false);

    function getCurrentDateTime() {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    }

    const schema = Yup.object().shape({
      dateOuverture: Yup.string().required("La date d'ouverture est obligatoire"),
      caisse: Yup.number().required('La caisse est obligatoire'),
      user: Yup.array()
        .min(1, 'Au moins une personne doit être sélectionnée')
        .required('Ce champ est obligatoire'),
      observations: Yup.string().notRequired(),
    });

    configure({
      validateOnBlur: true,
      validateOnChange: true,
      validateOnInput: true,
    });

    // GET /derniere/fermeture/caisse?caisseId=... -> { data: { premiereOuverture, soldeOuverture } }
    // - première ouverture : solde initial à saisir (valeur proposée = solde actuel de la caisse)
    // - sinon : solde compté à la dernière fermeture (non modifiable)
    const handleCaisseChange = async (newCaisseId: number | null) => {
      if (!newCaisseId) {
        soldeOuverture.value = 0;
        soldeDerniereFermeture.value = null;
        return;
      }
      try {
        const response = await ApiService.get(`/derniere/fermeture/caisse?caisseId=${newCaisseId}`);
        const propose = Number(response.data?.data?.soldeOuverture) || 0;
        soldeDerniereFermeture.value = response.data?.data?.premiereOuverture ? null : propose;
        soldeOuverture.value = propose;
      } catch (err: any) {
        soldeDerniereFermeture.value = null;
        soldeOuverture.value = 0;
        error(err.response?.data?.message || "Erreur lors de la récupération du solde d'ouverture de la caisse.");
      }
    };

    // POST /ouvFer -> { dateOuverture, caisseId, userIds, observations }
    const sendOuvFer = async (values: any, { resetForm }: any) => {
      if (isSubmitting.value) return;
      isSubmitting.value = true;

      try {
        const payload = {
          dateOuverture: dateOuverture.value,
          caisseId: caisse.value,
          userIds: [...new Set(user.value)],
          observations: values.observations,
          soldeInitial: Number(soldeOuverture.value),
        };

        await nextTick();

        const caisseName =
          caisses.value.find((c) => c && c.id?.toString() === caisse.value?.toString())?.nom ||
          'caisse inconnue';

        const result = await monSwal(
          `Vous êtes sur le point d'ouvrir la caisse ${caisseName} avec un solde d'ouverture de ${Number(soldeOuverture.value || 0).toFixed(2)} Fcfa. Êtes-vous sûr ?`,
          undefined,
          'btn btn-primary',
          undefined,
          undefined
        );

        if (result) {
          const response = await ApiService.post('/ouvFer', payload);
          success(response.data?.message || 'Ouverture de caisse enregistrée avec succès');
          resetForm();
          router.push('/OuverturesFermetures');
        }
      } catch (err: any) {
        console.error('Erreur createOuvFer :', err);
        error(err.response?.data?.message || "Erreur lors de l'ouverture de la caisse.");
      } finally {
        isSubmitting.value = false;
      }
    };

    // Le backend n'autorise l'ouverture que des caisses affectées à l'utilisateur
    // et non déjà ouvertes (caisse.statut === false).
    const getCaisse = async () => {
      try {
        const currentUserId = getUser();

        if (!currentUserId) {
          error('Utilisateur non connecté. Veuillez vous connecter.');
          return;
        }

        const { data } = await ApiService.get(`affectationCaisse/user/${currentUserId}`);
        const affectations = data.data || [];

        caisseOptions.value = affectations
          .filter((aff: any) => aff.caisse && !aff.caisse.statut)
          .map((aff: any) => ({
            label: aff.caisse.nom,
            value: aff.caisse.id,
          }));

        caisses.value = affectations.map((aff: any) => aff.caisse).filter(Boolean);
      } catch (err: any) {
        error(err.response?.data?.message || 'Erreur lors de la récupération des caisses');
      }
    };

    const getAllUsers = async () => {
      try {
        const { data } = await ApiService.get('/users');
        const donnees = data.data.data;
        userOptions.value = donnees.map((us: any) => ({
          value: us.id,
          label: `${us.nom} ${us.prenom}`,
        }));
      } catch (err: any) {
        error(err.response?.data?.message || 'Erreur lors de la récupération des utilisateurs');
      }
    };

    onMounted(async () => {
      await Promise.all([getCaisse(), getAllUsers()]);
    });

    return {
      user,
      userOptions,
      dateOuverture,
      currentDateTime,
      soldeOuverture,
      soldeDerniereFermeture,
      soldeModifie,
      soldeInvalide,
      caisse,
      caisseOptions,
      schema,
      sendOuvFer,
      handleCaisseChange,
      isSubmitting,
    };
  },
});
</script>

<style scoped>
.overview {
  margin: auto;
}
.text-title {
  text-align: center;
}
.overflow {
  padding: 20px 100px;
}
</style>